/**
 * ConnectShopifyCta — the placeholder card shown in place of the
 * Shopify-data-connector UI on the default Stripe build. Plan-
 * ShopifyIsolatedFrontend.md §3.1 locked behaviour: keep ONE marketing
 * CTA per surface that points merchants at `shop.prodagi.ai`. No
 * Shopify SDK / API / OAuth code runs from this component — it is a
 * plain external link so the Stripe bundle stays free of Shopify
 * network calls.
 *
 * 2026-07-06 owner revision: expanded from a slim link card into an
 * App-vs-Shop comparison so merchants understand (1) what each version
 * does, (2) that the Shopify App is the better fit for Shopify stores,
 * and (3) that the two are fully isolated environments — nothing
 * transfers between them.
 */
import React from 'react';
import { ShoppingBag, ExternalLink, Check, Plug, ShieldAlert } from 'lucide-react';
import { APP_CONFIG } from '../../config/appConfig';

// Copy is limited to SHIPPED features on each side — don't add rows here
// without verifying the feature exists on that distribution.
const SHOP_FEATURES = [
    'One-click install via Shopify OAuth — no manual API setup',
    'Full catalog sync with product search & recommendations built in',
    'Order lookup + draft-order checkout links in chat',
    'Add to cart from chat AND voice/avatar on your storefront',
    'Embedded store widget via the Shopify theme extension',
    'AI Stylist for outfit & look recommendations',
    'Product and order cards rendered automatically',
    'Billing handled inside Shopify (Managed Pricing)',
];

const APP_FEATURES = [
    'Custom API Integrations — the data connectors on this page',
    'AI-inferred card rendering for your connector results',
    'Connects your own store or any external, non-Shopify platform via API',
    'Billing via Stripe',
];

const FeatureList: React.FC<{ items: string[] }> = ({ items }) => (
    <ul className="space-y-1.5">
        {items.map((f) => (
            <li key={f} className="flex gap-2 text-xs text-gray-600 dark:text-gray-300">
                <Check className="w-3.5 h-3.5 shrink-0 mt-0.5 text-green-600 dark:text-green-400" />
                <span>{f}</span>
            </li>
        ))}
    </ul>
);

const ConnectShopifyCta: React.FC = () => {
    // Defense-in-depth — when this build IS the shop variant, don't
    // show the CTA (the real Shopify panel renders instead via the
    // gated dynamic import upstream).
    if (APP_CONFIG.billingProvider === 'shopify') return null;

    // Shop link is env-driven (APP_CONFIG.shopUrl) so staging vs prod
    // builds point at the right shop. Derive the host for display copy
    // so the visible text matches the configured environment instead of
    // a stale "shop.prodagi.ai"; fall back to the raw value if parsing
    // fails for any reason.
    let shopHost = APP_CONFIG.shopUrl;
    try {
        shopHost = new URL(APP_CONFIG.shopUrl).host;
    } catch {
        /* keep raw */
    }
    // This build's own host, for the comparison + isolation copy.
    const appHost = window.location.host;

    return (
        <div className="card p-6 space-y-4">
            {/* Header + CTA */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center shrink-0">
                    <ShoppingBag className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        Run a Shopify store? Use the Shopify App
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        AskProdigy ships in two versions. For Shopify stores, the dedicated
                        Shopify App gives you far more out of the box — with near-zero setup.
                    </p>
                </div>
                <a
                    href={APP_CONFIG.shopUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-xs flex items-center justify-center gap-1.5 self-start sm:self-auto w-full sm:w-auto"
                >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open {shopHost}
                </a>
            </div>

            {/* Side-by-side comparison — stacks at mobile width */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-xl border border-green-300 dark:border-green-800/70 bg-green-50/60 dark:bg-green-900/10 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                            <ShoppingBag className="w-4 h-4 shrink-0 text-green-600 dark:text-green-400" />
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Shopify App</p>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{shopHost}</p>
                            </div>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-600 text-white dark:bg-green-500 dark:text-green-950">
                            Recommended for Shopify
                        </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                        Purpose-built for Shopify stores — connects to your catalog and
                        orders the moment you install it.
                    </p>
                    <FeatureList items={SHOP_FEATURES} />
                </div>

                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <Plug className="w-4 h-4 shrink-0 text-primary" />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                This App version <span className="font-normal text-gray-500 dark:text-gray-400">(you are here)</span>
                            </p>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{appHost}</p>
                        </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                        For businesses outside Shopify — bring your own APIs and connect
                        them as live tools for your AI agents.
                    </p>
                    <FeatureList items={APP_FEATURES} />
                </div>
            </div>

            {/* Data isolation notice */}
            <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                <p>
                    <span className="font-semibold">Separate environments:</span>{' '}
                    <span className="font-mono">{appHost}</span> and{' '}
                    <span className="font-mono">{shopHost}</span> are completely
                    independent. Accounts, workspaces, agents, knowledge, integrations and
                    conversations do <span className="font-semibold">not</span> transfer or
                    sync between them — signing up on the Shopify App starts a fresh
                    workspace, and everything you build here stays here.
                </p>
            </div>
        </div>
    );
};

export default ConnectShopifyCta;
